import React from 'react'
import { Check, X, Sparkles } from 'lucide-react'

const Pricing = ({ trackEvent }) => {
  const tiers = [
    {
      name: 'Free',
      price: 'R0',
      period: '/month',
      description: 'Perfect for getting started',
      features: [
        { text: 'Unlimited Prayer Mode sessions', included: true },
        { text: 'Lock any apps during prayer', included: true },
        { text: '600+ guided devotions (streaming)', included: true },
        { text: 'Daily streak tracking', included: true },
        { text: 'Join 1 Prayer Pod', included: true },
        { text: 'Milestone badges (3, 7, 14, 30+ days)', included: true },
        { text: 'Offline downloads (10 devotions max)', included: true },
        { text: 'Sunday Prayer Stats', included: false },
        { text: 'Unlimited Pods', included: false },
        { text: 'Streak Freeze', included: false },
        { text: 'Advanced analytics', included: false },
      ],
      cta: 'Start Free Now',
      ctaVariant: 'secondary',
      popular: false,
    },
    {
      name: 'Premium',
      price: 'R99',
      period: '/month',
      altPrice: 'or R990/year (save R198)',
      description: 'For serious spiritual growth',
      features: [
        { text: 'Everything in Free, plus:', included: true, bold: true },
        { text: 'Sunday Prayer Stats (weekly analytics email + dashboard)', included: true },
        { text: 'Unlimited Prayer Pods (create as many as you want)', included: true },
        { text: 'Streak Freeze (1 per month - preserve your streak)', included: true },
        { text: '50 offline downloads (vs. 10 in Free)', included: true },
        { text: 'Mood tracking (log how you feel post-prayer)', included: true },
        { text: 'Export prayer history (CSV download)', included: true },
        { text: 'Early access to new devotions', included: true },
        { text: 'Priority support (email responses within 24hrs)', included: true },
      ],
      cta: 'Try 7 Days Free',
      ctaVariant: 'primary',
      popular: true,
      riskReversal: 'Cancel anytime. No questions asked.',
    },
  ]

  return (
    <section id="pricing" className="section-padding bg-gradient-to-b from-grace-gray to-white">
      <div className="container-custom">
        {/* Headline */}
        <div className="text-center mb-16 animate-on-scroll">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-heading-dark mb-4">
            Start Free. <span className="text-gradient">Upgrade When You're Ready</span>.
          </h2>
          <p className="text-lg md:text-xl text-body-gray max-w-3xl mx-auto">
            No credit card required. No bait-and-switch. Prayer Mode, Streaks,
            and Devotions are free forever. Premium unlocks analytics & unlimited Pods.
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto mb-12">
          {tiers.map((tier, index) => (
            <div
              key={index}
              className={`card relative animate-on-scroll ${
                tier.popular ? 'ring-2 ring-prayer-blue shadow-2xl' : ''
              }`}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              {/* Popular Badge */}
              {tier.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <div className="bg-streak-gold text-white text-sm font-bold py-2 px-6 rounded-full flex items-center gap-2 shadow-lg animate-pulse-slow">
                    <Sparkles size={16} />
                    Most Popular
                  </div>
                </div>
              )}

              {/* Tier Header */}
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-heading-dark mb-2">{tier.name}</h3>
                <div className="flex items-baseline justify-center gap-1 mb-2">
                  <span className={`text-5xl font-bold ${tier.popular ? 'text-prayer-blue' : 'text-heading-dark'}`}>
                    {tier.price}
                  </span>
                  <span className="text-lg text-muted-gray">{tier.period}</span>
                </div>
                {tier.altPrice && (
                  <div className="text-sm text-pod-green font-semibold mb-2">{tier.altPrice}</div>
                )}
                <p className="text-body-gray">{tier.description}</p>
              </div>

              {/* Features List */}
              <ul className="space-y-3 mb-8">
                {tier.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-1">
                      {feature.included ? (
                        <Check size={20} className="text-success" />
                      ) : (
                        <X size={20} className="text-muted-gray" />
                      )}
                    </div>
                    <span className={`text-sm ${feature.bold ? 'font-semibold text-heading-dark' : 'text-body-gray'}`}>
                      {feature.text}
                    </span>
                  </li>
                ))}
              </ul>

              {/* CTA Button */}
              <a
                href="#download"
                onClick={() => trackEvent('pricing_cta_click', { tier: tier.name })}
                className={`w-full block text-center ${tier.ctaVariant === 'primary' ? 'btn-primary' : 'btn-secondary'}`}
              >
                {tier.cta}
              </a>

              {/* Risk Reversal */}
              {tier.riskReversal && (
                <p className="text-center text-sm text-muted-gray italic mt-4">
                  {tier.riskReversal}
                </p>
              )}
            </div>
          ))}
        </div>

        {/* Comparison Table */}
        <div className="max-w-4xl mx-auto animate-on-scroll">
          <h3 className="text-2xl font-bold text-center text-heading-dark mb-8">
            Feature Comparison
          </h3>
          <div className="bg-white rounded-xl shadow-card overflow-hidden">
            <table className="w-full">
              <thead className="bg-grace-gray">
                <tr>
                  <th className="text-left py-4 px-6 font-semibold text-heading-dark">Feature</th>
                  <th className="text-center py-4 px-6 font-semibold text-heading-dark">Free</th>
                  <th className="text-center py-4 px-6 font-semibold text-prayer-blue">Premium</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-grace-gray">
                {[
                  { feature: 'Prayer Mode & Streaks', free: 'Unlimited', premium: 'Unlimited' },
                  { feature: 'Guided Devotions', free: '600+', premium: '600+ (early access)' },
                  { feature: 'Prayer Pods', free: '1 Pod', premium: 'Unlimited' },
                  { feature: 'Offline Downloads', free: '10 devotions', premium: '50 devotions' },
                  { feature: 'Sunday Prayer Stats', free: '-', premium: 'Included' },
                  { feature: 'Streak Freeze', free: '-', premium: '1/month' },
                  { feature: 'Analytics & Exports', free: '-', premium: 'Included' },
                ].map((row, idx) => (
                  <tr key={idx}>
                    <td className="py-4 px-6 text-body-gray">{row.feature}</td>
                    <td className="py-4 px-6 text-center text-sm">{row.free}</td>
                    <td className="py-4 px-6 text-center text-sm font-semibold text-prayer-blue">{row.premium}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-12 animate-on-scroll">
          <a
            href="#download"
            onClick={() => trackEvent('pricing_bottom_cta_click')}
            className="btn-primary text-lg inline-block"
          >
            Start Your Free Trial
          </a>
          <p className="text-sm text-muted-gray mt-4">No credit card required for Free tier</p>
        </div>
      </div>
    </section>
  )
}

export default Pricing
